import betongAvtaleImg from "@/assets/blog/historisk-gronn-betongavtale.webp";
import jordvaskImg from "@/assets/blog/aapning-jordvaskeanlegg.webp";
import dokumentertImg from "@/assets/blog/dokumentert-sirkulaer-masse.webp";
import gravemaskinImg from "@/assets/blog/gravemaskin.webp";
import lonnsomtImg from "@/assets/blog/lonnsomt.webp";
import ansatteImg from "@/assets/blog/ansatte.webp";
import rotevatnImg from "@/assets/rotevatn.webp";
import betongAvtaleImgThumb from "@/assets/blog/thumbs/historisk-gronn-betongavtale.webp";
import jordvaskImgThumb from "@/assets/blog/thumbs/aapning-jordvaskeanlegg.webp";
import dokumentertImgThumb from "@/assets/blog/thumbs/dokumentert-sirkulaer-masse.webp";
import gravemaskinImgThumb from "@/assets/blog/thumbs/gravemaskin.webp";
import lonnsomtImgThumb from "@/assets/blog/thumbs/lonnsomt.webp";
import ansatteImgThumb from "@/assets/blog/thumbs/ansatte.webp";

export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  image: string;
  /** Smaller variant for listing cards */
  thumb?: string;
  tag: string;
  imageCaption?: string;
  content: string;
}

export const blogPosts: BlogPost[] = [
  {
    slug: "dokumentert-sirkulaer-masse",
    title: "Derfor vinner entreprenører flere anbud med dokumentert sirkulær masse",
    excerpt: "Anbudsmarkedet har endret seg. Miljø vektes ofte med 20–40 prosent. Entreprenører som bruker massehåndtering strategisk bygger sterkere anbud.",
    date: "2026-03-03",
    image: dokumentertImg,
    thumb: dokumentertImgThumb,
    tag: "Innsikt",
    content: `Anbudsmarkedet i bygg- og anleggsbransjen har endret seg. Det er ikke lenger tilstrekkelig å levere riktig pris og realistisk fremdrift. Offentlige byggherrer, kommuner, statlige aktører og større private utviklere stiller nå tydelige krav til klimaregnskap, dokumentasjon, EPD, reduserte utslipp og sporbarhet i leverandørkjeden. Miljø vektes ofte med 20–40 prosent i evalueringen. I enkelte konkurranser enda mer.

Dette betyr at bærekraft ikke lenger er en støttefunksjon i tilbudet – det er en del av konkurransegrunnlaget.

Entreprenører som forstår dette, bruker massehåndtering strategisk. Dokumentert sirkulær masse gir ikke bare et grønnere prosjekt, men et sterkere og mer attraktivt anbud.

## Dokumentasjon slår intensjon

Byggherrer etterspør i dag tall, ikke formuleringer. Det holder ikke å skrive at man "tilstreber bærekraftige løsninger". De vil se konkrete reduksjoner i utslipp, dokumenterte materialstrømmer og tall som kan føres direkte inn i prosjektets klimaregnskap.

Tradisjonell massehåndtering innebærer ofte lang transport, bruk av jomfruelige masser og begrenset dokumentasjon på miljøpåvirkning. Dette gir høyere utslipp og svakere konkurransekraft i anbud der miljø teller tungt.

Når entreprenører benytter dokumentert sirkulær masse, kan de vise til:

- Reduserte transportrelaterte CO₂-utslipp
- Sporbar og dokumentert materialbruk
- Bidrag til byggherrens klimamål og ESG-rapportering
- Konkrete tall som styrker miljøvektingen i evalueringen

Dette er målbare forbedringer – og målbarhet er avgjørende i moderne anbud.

## Miljøprestasjon påvirker totalvurderingen

I mange konkurranser ligger prisene tett. Differansen mellom første- og andreplass kan være marginal. Når miljø vektes tungt, kan forbedringer i klimaregnskapet være det som tipper avgjørelsen.

Sirkulær masse bidrar direkte til å styrke tilbudet ved å:

- Redusere prosjektets totale klimafotavtrykk
- Forbedre poengscore på miljøkriterier
- Dokumentere ansvarlig ressursbruk
- Vise fremtidsrettet og strategisk forståelse

Dette sender et tydelig signal til byggherre: Entreprenøren har kontroll på leverandørkjeden og tar miljøkrav på alvor.

## ESG er blitt en del av forretningen

Større aktører opererer nå med klare ESG-mål og rapporteringskrav. Scope 3-utslipp, materialgjenvinning og sirkulær økonomi er ikke lenger temaer for bærekraftsrapporter alene – de påvirker investeringsbeslutninger, omdømme og samarbeidspartnere.

Entreprenører som kan dokumentere bruk av sirkulære løsninger, bidrar direkte til byggherrens måloppnåelse. Det gjør tilbudet mer attraktivt, ikke bare i selve konkurransen, men også i videre samarbeid og fremtidige prosjekter.

## Fra kostnad til konkurransefortrinn

Massehåndtering har tradisjonelt blitt sett på som en logistisk nødvendighet – en kostnad som må håndteres mest mulig effektivt. I dagens marked bør det betraktes som en strategisk beslutning.

Riktig valg av massehåndtering kan:

- Styrke prosjektets klimaregnskap
- Øke anbudets samlede konkurransekraft
- Redusere transportrelaterte kostnader
- Skape merverdi for byggherre

Når miljø er en del av konkurransegrunnlaget, blir bærekraft en del av lønnsomhetsregnestykket.

## Et tydelig konkurransefortrinn

Entreprenører som integrerer dokumentert sirkulær masse i sine prosjekter, oppnår mer enn en grønn profil. De bygger referanser, dokumentasjon og kompetanse som kan brukes aktivt i fremtidige konkurranser.

I et marked der kravene øker og marginene presses, handler det om å differensiere seg. Dokumentert sirkulær masse er ikke lenger et idealistisk valg – det er en konkret måte å styrke tilbudet, redusere risiko og øke sannsynligheten for å vinne.

Spørsmålet er derfor ikke om bærekraft påvirker anbud. Spørsmålet er om tilbudet ditt er rustet for det.`,
  },
  {
    slug: "lidenskap-moter-anleggsmaskiner",
    title: "Når lidenskap møter anleggsmaskiner",
    excerpt: "Hos Envir handler det ikke bare om maskiner og masser – det handler også om menneskene bak. Historien bak vår nye gravemaskin med en helt spesiell foliering.",
    date: "2026-02-15",
    image: gravemaskinImg,
    thumb: gravemaskinImgThumb,
    tag: "Kultur",
    content: `I bygg- og anleggsbransjen handler mye om maskiner, effektivitet og logistikk. Gravemaskiner, lastebiler og sorteringsutstyr er verktøyene som gjør jobben mulig. Men noen ganger kan også maskiner fortelle en historie. Hos Envir ønsker vi ikke bare å utvikle mer bærekraftige løsninger for massehåndtering – vi ønsker også å bygge en kultur hvor menneskene bak arbeidet blir synlige. Derfor fikk en av gravemaskinene våre nylig en helt spesiell foliering.

Den nye grafikken er inspirert av et sitat fra fotballklubben Leeds United. For mange utenforstående kan dette virke som en liten detalj, men for oss symboliserer det noe større. Arbeidet vi gjør i Envir er et resultat av mennesker med lidenskap for både fag og arbeidsplass. Når maskinen nå står ute på anlegg, er det ikke bare en gravemaskin – det er også en påminnelse om menneskene som bidrar til å utvikle selskapet og bransjen.

## Kultur på byggeplassen

Byggeplasser kan være hektiske og krevende arbeidsmiljøer. Samtidig er det nettopp der samarbeid, stolthet og lagånd oppstår. Ved å gi maskinen en unik identitet ønsket vi å markere at bransjen vår ikke bare handler om maskiner og masser – den handler også om mennesker. Historier, engasjement og fellesskap er en viktig del av kulturen vi ønsker å bygge videre på hos Envir.

Gravemaskinen er selvfølgelig først og fremst et arbeidsverktøy. Den er en del av arbeidet med å håndtere masser på en mer bærekraftig måte, redusere transport og bidra til sirkulær ressursbruk i byggeprosjekter. Men samtidig viser den hvordan små initiativ kan bidra til en sterkere identitet og stolthet i organisasjonen.

## Tre ting denne maskinen symboliserer for oss

- Kultur og lagånd: Byggeplasser fungerer best når mennesker trives og føler tilhørighet.
- Synlighet i bransjen: Folieringen gjør Envir mer synlig ute på prosjekter og byggeplasser.
- Stolthet i arbeidet: Små historier kan gi stor betydning for arbeidsmiljø og fellesskap.

Hos Envir tror vi at fremtidens byggebransje ikke bare handler om teknologi og bærekraftige løsninger. Den handler også om menneskene som står bak. Hvis du vil vite mer om hvordan vi jobber med sirkulær massehåndtering – eller bare ønsker å se maskinen vår ute på et prosjekt – er du alltid velkommen til å ta kontakt.`,
  },
  {
    slug: "historisk-gronn-betongavtale",
    title: "Historisk grønn betongavtale",
    excerpt: "Ølen Betong og Envir har inngått en historisk avtale som skal gjøre betongen på Vestlandet vesentlig mer klimavennlig med kortreiste gjenvunne masser.",
    date: "2026-01-26",
    image: betongAvtaleImg,
    thumb: betongAvtaleImgThumb,
    tag: "Nyhet",
    imageCaption: "Fra venstre: Kolbjørn Akervold, daglig leder Envir, Nina Rebnord, leder salg og marked Envir, Knut Ståle Lyngstad, regionleder Vestland og Truls Krossøy, teknolog Vestland Ølen Betong. Foto: Paul S Amundsen",
    content: `Ølen Betong og Envir har inngått en historisk avtale som skal gjøre betongen som brukes på Vestlandet vesentlig mer klimavennlig.

Kortreiste gjenvunne masser, vasket i Envirs anlegg i Bergen, skal brukes i betongen. Dermed reduseres klimaavtrykket i produktene fra Ølen Betong betydelig.

> «Dette er et viktig steg mot mer sirkulær og bærekraftig bruk av ressurser i bygg- og anleggsbransjen på Vestlandet i årene som kommer.»
> — Kolbjørn Akervold, daglig leder i Envir

Tilslag (sand, pukk, grus og annet) utgjør rundt 70 prosent av betongens volum, og har dermed stor betydning for både klima- og naturpåvirkning. Ved å øke andelen gjenvunne og vaskede masser, reduseres behovet for uttak av jomfruelige ressurser betydelig.

Gjenbruk er en sentral milepæl i utviklingen av mer sirkulær betong, og samarbeidet mellom Ølen Betong og Envir viser hvordan dette kan realiseres i praksis. Løsningen peker fremover mot fremtidens betong og er i tråd med den nasjonale retningen for økt sirkularitet som nå diskuteres gjennom regjeringens pågående høring.

> «Gjennom DØNN-partnerportalen får kundene enkel og transparent innsikt i sitt klimaregnskap. Portalen viser både gjenbruksgrad på betongen og konkret CO₂-besparelse, noe som gjør det lettere å dokumentere miljøeffekt og nå egne bærekraftsmål.»
> — Knut Ståle Lyngstad, regionsleder Vestland, Ølen Betong

Avtalen reduserer uttaket av jomfruelige masser fra naturen og dermed store naturinngrep. De vaskede gjenvunne massene holder høy kvalitet og kan brukes i de fleste typer betong.

Det har vært stort fokus på betong som en av flere «klimaverstinger» de siste årene. Samtidig er samfunnet helt avhengig av betongproduksjon. Bynære gjenvinningsanlegg og betongprodusenter er i kombinasjon viktig for å få gjennomført det grønne skifte.

> «Dette er en stor dag for en liten grønn bedrift på Laksevåg i Bergen og en fantastisk anerkjennelse for arbeidet vi har lagt ned gjennom flere år. Ølen Betong er en seriøs aktør som har fulgt oss lenge, heiet på oss og har sett verdien av det vi har forsøkt å få til.»
> — Kolbjørn Akervold, daglig leder i Envir

## Om Ølen Betong

Ølen Betong AS er en av Norges ledende leverandører av ferdigbetong, betongvarer og betongelementer til tunnel og samferdsel. Hovedkontoret er i Ølensvåg, fem mil øst for Haugesund. Selskapet er en del av Nordic Concrete Group (NCG), med eierinteresser i over 60 betongstasjoner i Norge.

## Om Envir

Envir AS ble grunnlagt i 2017. Selskapet tar imot forurenset jord og gravemasser og selger gjenvunne masser videre. Envir har et av de mest komplette anleggene i verden for rensing og behandling av masser.`,
  },
  {
    slug: "sirkulaere-masser-lonnsomt",
    title: "Hvorfor sirkulære masser kan gjøre prosjektet ditt mer lønnsomt",
    excerpt: "Massehåndtering trenger ikke bare være en kostnad. Med sirkulære masser kan entreprenører redusere transport, styrke anbud og øke lønnsomheten.",
    date: "2025-11-12",
    image: lonnsomtImg,
    thumb: lonnsomtImgThumb,
    tag: "Innsikt",
    content: `I mange byggeprosjekter blir massehåndtering sett på som en ren kostnad. Masser skal flyttes, transporteres og håndteres raskt slik at prosjektet kan gå videre. Men i takt med økende klimakrav og strengere dokumentasjonskrav har dette perspektivet begynt å endre seg. Flere entreprenører oppdager nå at måten masser håndteres på kan ha stor betydning både for prosjektets økonomi og konkurransekraft.

Tradisjonell massehåndtering innebærer ofte transport av store mengder masser over lange avstander, samtidig som nye jomfruelige masser må fraktes inn. Dette skaper betydelige transportkostnader og øker prosjektets klimaavtrykk. Ved å benytte sirkulære masser – masser som renses, behandles og gjenbrukes – kan man redusere både transportbehov og ressursforbruk. Resultatet er ofte lavere kostnader og et mer bærekraftig prosjekt.

## Bærekraft som konkurransefortrinn

I tillegg spiller bærekraft en stadig større rolle i anbudskonkurranser. Offentlige byggherrer og større private aktører stiller tydelige krav til klimaregnskap, dokumentasjon og reduserte utslipp. Entreprenører som kan vise til bruk av sirkulære løsninger, står derfor sterkere i konkurranser hvor miljø vektes høyt. På den måten kan sirkulær massehåndtering bidra både til direkte kostnadsbesparelser og til økt sannsynlighet for å vinne nye prosjekter.

Det er nettopp derfor stadig flere entreprenører begynner å se massehåndtering som en strategisk del av prosjektet. Når masser behandles som en ressurs fremfor et avfallsproblem, åpner det også for nye muligheter i prosjektplanleggingen.

## Tre økonomiske fordeler ved sirkulær massehåndtering

- Lavere transportkostnader: Mindre behov for langtransport av masser gir direkte økonomiske besparelser.
- Sterkere anbud: Dokumentert bærekraft kan gi bedre score i konkurranser.
- Bedre ressursutnyttelse: Masser som tidligere ble sett på som avfall kan bli en verdifull ressurs.

Hos Envir jobber vi hver dag med å gjøre det enklere for entreprenører å ta i bruk sirkulære masser. Ta gjerne kontakt dersom du ønsker å diskutere hvordan mer sirkulær massehåndtering kan bidra til både bærekraft og lønnsomhet i ditt neste prosjekt.`,
  },
  {
    slug: "byggebransjen-klimamal",
    title: "Hvordan byggebransjen kan nå klima- og bærekraftsmålene",
    excerpt: "Byggebransjen må endre måten den planlegger, bygger og håndterer ressurser på. Sirkulær økonomi og bedre dokumentasjon er nøkkelen.",
    date: "2025-09-20",
    image: rotevatnImg,
    tag: "Innsikt",
    content: `Bygge- og anleggsbransjen spiller en avgjørende rolle i arbeidet med å redusere klimagassutslipp. Bransjen står for en betydelig del av verdens ressursbruk og utslipp, samtidig som den legger grunnlaget for infrastrukturen samfunnet er avhengig av. Derfor er det bred enighet om at sektoren må endre måten den planlegger, bygger og håndterer ressurser på dersom klima- og bærekraftsmålene skal nås.

## Fra lineær til sirkulær økonomi

Et av de viktigste grepene er å gå fra en lineær til en sirkulær økonomi. I stedet for å hente ut nye råvarer, bruke dem én gang og deretter deponere dem, handler sirkulær økonomi om å gjenbruke og resirkulere materialer så langt det er mulig. For byggebransjen betyr dette blant annet bedre håndtering av masser, mer gjenbruk av materialer og mer effektiv logistikk.

Samtidig øker kravene til dokumentasjon og klimaregnskap. Byggherrer etterspør konkrete tall på utslipp og materialbruk, og stadig flere prosjekter krever dokumentasjon på hvordan ressursene håndteres. Dette gjør at entreprenører som allerede jobber med sirkulære løsninger står bedre rustet for fremtidens krav.

Overgangen til en mer bærekraftig byggebransje handler derfor ikke bare om teknologi, men også om nye arbeidsmåter og bedre samarbeid i hele verdikjeden. Fra planlegging til gjennomføring må bærekraft integreres i beslutningene som tas.

## Tre viktige grep byggebransjen kan ta

- Mer sirkulær ressursbruk: Gjenbruk og behandling av masser reduserer behovet for nye råvarer.
- Bedre klimadokumentasjon: Tydelig datagrunnlag gjør det mulig å redusere utslipp systematisk.
- Tettere samarbeid i verdikjeden: Entreprenører, leverandører og byggherrer må jobbe mot samme mål.

Hos Envir jobber vi med løsninger som gjør sirkulær massehåndtering mulig i praksis. Hvis du ønsker å vite mer om hvordan slike løsninger kan bidra til å redusere utslipp i byggeprosjekter, tar vi gjerne en prat.`,
  },
  {
    slug: "aapning-av-jordvaskeanlegg",
    title: "Åpning av jordvaskeanlegg",
    excerpt: "«Takk for at dere gjør dette! Dette er alt annet enn symbolpolitikk,» sa byrådsleder Christine Meyer da hun åpnet Envirs nye jordvaskeanlegg.",
    date: "2024-09-05",
    image: jordvaskImg,
    thumb: jordvaskImgThumb,
    tag: "Nyhet",
    imageCaption: "Byrådsleder Christine Meyer åpner Envirs jordvaskeanlegg i Bergen.",
    content: `## «Takk for at dere gjør dette!»

> «Dette er alt annet enn symbolpolitikk.»
> — Christine Meyer, byrådsleder i Bergen

Dette sa byrådsleder Christine Meyer da hun åpnet Envirs nye jordvaskeanlegg i Bergen torsdag 22. august 2024.

Anlegget kan gjenvinne over 1000 tonn om dagen av jord, sand og grus fra forurensede gravemasser – masser som ellers går på deponi. I tonn er det mer enn alt husholdningsavfallet i Bergen.

Denne investeringen er viktig for å nå Bergens mål om 65 % materialgjenvinning av avfall innen 2030. Gjenvinningsgraden til Envirs jordvaskeanlegg er på 80 %, mens snittet for gjenvinning av lett forurensede masser (LFM) i Norge er 1,1 %.

Byrådslederen var stolt over å lede en by med så engasjerte bedrifter og lovte å føre en politikk som legger til rette for de som går i front. Fordi, dette er alt annet enn symbolpolitikk. Dette er lønnsomt, viktig og gjør en forskjell.

Hovedeier Simon Simonnæs sa han for få år siden ikke hadde tenkt mye på at Norge er dårligst i Europa på sirkulærøkonomi, helt til forskningslederen i Envir fortalte han at det tar ett tusen år å lage en centimeter ny matjord. Over halvparten av avfallet fra byggebransjen som havner på deponi er lettforurensede jord- og gravemasser.

> «Skal vi klare å kutte 55 % i klimaavtrykket før 2030 er gjenbruk av masser det viktigste tiltaket.»
> — Espen Alvestad, fagsjef gjenvinning, Maskinentreprenørenes forbund

> «Det er en glede å følge med på Envir og det engasjementet de har for å implementere bærekraft-standard i bransjen, og samtidig vise med handling at de mener det.»
> — Anita Helene Hall, generalsekretær, Norsk Bergindustri

Sammen med laboratorium og jorddamper for biologisk forurenset jord kan Envir tilby et av de mest komplette anleggene som fins for håndtering av forurenset jord- og gravemasser i verden.

> «Det er ett av de mest komplette anleggene i hele verden. Det er laget spesielt for masser fra Bergensområdet, alt går på strøm og det er støydempet av hensyn til naboene.»
> — Audun Ørbeck, Salgs & prosjektleder, Mineralteknikk AS

Anlegget ligger rundt åtte minutters kjøretur fra Bergen sentrum, på Laksevåg, i den gamle hallen til Blikkvalseverket fra 1916, tredd innimellom gamle fagverk og kranbaner.

> «Vi er stolt av å ligge på Laksevåg, og håper Laksevåg blir stolt av oss.»
> — Kolbjørn Akervold, daglig leder i Envir

Prosessen med å få CE-merking på de gjenvunne fraksjonene er godt i gang. Via en ny dataplattform vil kundene få tilgang til klimaavtrykket, inkludert for transport til og fra, og vil også kunne beregne klimaavtrykk for prosjekter de har under planlegging.

Anlegget har ingen utslipp til luft eller sjø. Vannet som brukes er sjøvann fra byfjorden som avsaltes med omvendt osmose.

Anlegget har kostet i overkant av 100 millioner kroner inkludert ombygging av hallen, laboratorium, omvendt osmose-anlegg og dataplattform. Fra før er det investert anslagsvis 25 millioner kroner i jorddampings-anlegget medregnet rundt 10 millioner kroner i støtte fra Innovasjon Norge og Skattefunn.`,
  },
  {
    slug: "menneskene-bak-sirkulaer-masse",
    title: "Menneskene som gjør sirkulær masse mulig",
    excerpt: "Bak teknologi og prosesser finnes det mennesker som sørger for at arbeidet blir gjennomført. Møt teamet på jordrenseanlegget.",
    date: "2025-06-10",
    image: ansatteImg,
    thumb: ansatteImgThumb,
    tag: "Kultur",
    content: `Når man snakker om sirkulær økonomi i bygg- og anleggsbransjen, handler samtalen ofte om teknologi, maskiner og prosesser. Men bak alle systemene finnes det mennesker som sørger for at arbeidet faktisk blir gjennomført. På jordrenseanlegget hos Envir jobber et team av fagfolk hver dag med å håndtere, analysere og behandle masser slik at de kan brukes på nytt.

Arbeidet på et jordrenseanlegg krever både teknisk forståelse og praktisk erfaring. Masser som ankommer anlegget kan ha svært ulike egenskaper, og det er viktig å analysere og håndtere dem riktig. Teamet jobber med alt fra sortering og behandling til kontroll og dokumentasjon, slik at massene kan inngå i nye prosjekter på en trygg og bærekraftig måte.

## Mer enn bare en jobb

For de som jobber på anlegget er dette mer enn bare en jobb. Mange beskriver arbeidet som en mulighet til å bidra til en mer bærekraftig byggebransje. Ved å behandle masser som en ressurs fremfor et avfallsproblem, er de med på å redusere transport, spare naturressurser og gjøre byggeprosjekter mer klimavennlige.

Arbeidsdagen på anlegget kan variere mye. Noen dager handler det om store leveranser av masser som skal behandles raskt og effektivt. Andre dager går mer tid til analyser, planlegging og logistikk. Felles for alle dagene er at teamet jobber tett sammen for å sikre kvalitet og sikkerhet i prosessene.

## Tre ting som kjennetegner arbeidet på jordrenseanlegget

- Fagkompetanse: Arbeidet krever både teknisk kunnskap og praktisk erfaring.
- Samarbeid: Teamet jobber tett sammen for å håndtere store mengder masser effektivt.
- Bærekraft i praksis: Hver dag bidrar arbeidet til mer sirkulær ressursbruk i byggebransjen.

Hos Envir er vi stolte av menneskene som står bak arbeidet vårt. Det er deres kompetanse, engasjement og erfaring som gjør det mulig å utvikle mer bærekraftige løsninger for bransjen. Hvis du vil vite mer om arbeidet vårt – eller er interessert i å bli en del av teamet – er du alltid velkommen til å ta kontakt.`,
  },
];

export const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString("nb-NO", { day: "numeric", month: "long", year: "numeric" });
};
